import React, { useContext } from 'react';
import { motion } from 'framer-motion';
import { Github, Linkedin, Mail, ArrowDown, Sparkles } from 'lucide-react';
import { ThemeContext } from '../pages/_app';
import { heroData, contactInfo } from '../data/siteData';
import profileImage from '../img/Elenkasan.jpg';

const Home = () => {
  const { setActiveSection } = useContext(ThemeContext);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <section id="home" className="home-section">
      <motion.div 
        className="home-container"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Profile Image with Glow Effect */}
        <motion.div 
          className="profile-container"
          variants={itemVariants}
          whileHover={{ scale: 1.05 }}
          transition={{ type: "spring", stiffness: 300, damping: 10 }}
        >
          <div className="profile-glow"></div>
          <img 
            src={profileImage} 
            alt="Elena Nurullina" 
            className="profile-image"
          />
          <motion.div 
            className="profile-ring"
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          >
            <Sparkles className="sparkle sparkle-1" />
            <Sparkles className="sparkle sparkle-2" />
            <Sparkles className="sparkle sparkle-3" />
          </motion.div>
        </motion.div>

        {/* Name and Title */}
        <motion.h1 
          className="home-name"
          variants={itemVariants}
        >
          Elena Nurullina
        </motion.h1>

        {/* Hero Title */}
        <motion.div 
          className="hero-title-container"
          variants={itemVariants}
        >
          <span className="hero-title">{heroData.title}</span>
        </motion.div>

        {/* Subtitle */}
        <motion.p 
          className="hero-subtitle"
          variants={itemVariants}
        >
          {heroData.subtitle}
        </motion.p>

        {/* Tagline */}
        <motion.p 
          className="hero-tagline"
          variants={itemVariants}
        >
          {heroData.tagline}
        </motion.p>

        {/* Professional Summary */}
        <motion.div 
          className="hero-summary"
          variants={itemVariants}
        >
          {heroData.summary.split('\n\n').map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </motion.div>

        {/* Social Links */}
        <motion.div 
          className="home-social"
          variants={itemVariants}
        >
          <motion.a
            href={contactInfo.github}
            target="_blank"
            rel="noopener noreferrer"
            className="social-link"
            whileHover={{ scale: 1.1, rotate: 5 }}
            whileTap={{ scale: 0.95 }}
          >
            <Github aria-hidden="true" /><span className="sr-only">GitHub profile</span>
          </motion.a>
          <motion.a
            href={contactInfo.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="social-link"
            whileHover={{ scale: 1.1, rotate: -5 }}
            whileTap={{ scale: 0.95 }}
          >
            <Linkedin aria-hidden="true" /><span className="sr-only">LinkedIn profile</span>
          </motion.a>
          <motion.a
            href={`mailto:${contactInfo.email}`}
            className="social-link"
            whileHover={{ scale: 1.1, rotate: 5 }}
            whileTap={{ scale: 0.95 }}
          >
            <Mail aria-hidden="true" /><span className="sr-only">Email Elena</span>
          </motion.a>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div 
          className="home-cta"
          variants={itemVariants}
        >
          <motion.a
            href="#projects"
            className="cta-primary"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              setActiveSection && setActiveSection('projects');
              const section = document.getElementById('projects');
              if (section) {
                section.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            View My Work
          </motion.a>
          <motion.a
            href={contactInfo.resume}
            target="_blank"
            rel="noopener noreferrer"
            className="cta-secondary"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Download Resume
          </motion.a>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.a
          className="scroll-indicator"
          variants={itemVariants}
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          href="#industry" aria-label="Explore industry experience"
        >
          <ArrowDown />
          <span>Scroll to explore</span>
        </motion.a>
      </motion.div>
    </section>
  );
};

export default Home;